import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {gql} from "@apollo/client";
import {parseMimeType, PlayableMediaType} from "../../../utils";
import {SystemAction} from "../../../schema";
import {getTextWithStressMarks} from "../../Phrase";

import Bubble from "../Bubble";

import cn from "classnames";
import classes from "./SystemBubble.module.css";

type QueryData = {
  action: Pick<SystemAction, "audio" | "text" | "media" | "mediaType">
}

type props = React.ComponentProps<typeof Bubble> & {
  action: QueryData["action"],
  onPlayClick?: React.MouseEventHandler,
  levelsCount?: number,
}

export default function SystemBubble({action, className, onPlayClick, levelsCount, ...props}: props) {
  const {t} = useTranslation();

  const text = useMemo(() => getTextWithStressMarks(action.text), [action.text])

  const {media, mediaType} = action;
  const mediaPreview = useMemo(() => {
    if (media && mediaType) {
      switch (parseMimeType(mediaType).type) {
        case PlayableMediaType.IMAGE:
          return (
            <img
              className={classes.mediaPreview}
              src={media}
              alt={t("components.SystemBubble.imageAlt")}
            />
          )
        case PlayableMediaType.VIDEO:
          return (
            <video
              className={classes.mediaPreview}
              src={media}
            />
          )
        default:
          console.error(`Unsupported media type in system bubble: ${mediaType}`)
      }
    }
  }, [media, mediaType, t])

  return (
    <Bubble className={cn(classes.root, className)} {...props}>
      <Bubble.Row>
        <Bubble.Content>
          {text || (
            <Bubble.Placeholder>{t("components.SystemBubble.placeholder")}</Bubble.Placeholder>
          )}
          {mediaPreview}
        </Bubble.Content>

        {!!action.audio && (
          <Bubble.PlayButton onClick={onPlayClick}/>
        )}
      </Bubble.Row>
    </Bubble>
  )
}


SystemBubble.fragments = {
  root: gql`
    fragment SystemBubble on SystemAction {
      id
      text
      media
      mediaType
      audio
    }
  `,
}
